import { useState, useRef } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Upload, Trash2, FileSpreadsheet } from 'lucide-react'

interface ParticipantsUploadProps {
  onUpload: (file: File) => void
  onDelete: () => void
  isUploading: boolean
  isDeleting: boolean
  totalParticipantes: number
  camposExcel: string[]
  hideCard?: boolean
}

export function ParticipantsUpload({
  onUpload,
  onDelete,
  isUploading,
  isDeleting,
  totalParticipantes,
  camposExcel,
  hideCard,
}: ParticipantsUploadProps) {
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFile = (file: File) => {
    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      onUpload(file)
    }
  }

  const content = (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-base font-semibold flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-teal-500" />
          Agregar participantes
        </div>
        {totalParticipantes > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
            onClick={onDelete}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            {isDeleting ? 'Eliminando...' : 'Borrar todos'}
          </Button>
        )}
      </div>
      <div
        className={`
          border-2 border-dashed rounded-lg p-3 text-center transition-colors cursor-pointer
          ${dragOver
            ? 'border-teal-500 bg-teal-500/5'
            : 'border-gray-300 dark:border-gray-600 hover:border-teal-400'
          }
        `}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          const file = e.dataTransfer.files[0]
          if (file) handleFile(file)
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="flex items-center justify-center gap-2">
          <Upload className="h-4 w-4 text-muted-foreground" />
          <p className="text-sm font-medium">
            {isUploading ? 'Cargando...' : 'Arrastra Excel o haz clic'}
          </p>
        </div>
        {camposExcel.length > 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            Columnas: {camposExcel.join(', ')}
          </p>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) handleFile(file)
          e.target.value = ''
        }}
      />
    </>
  )

  if (hideCard) {
    return <div>{content}</div>
  }

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardContent className="pt-4">
        {content}
      </CardContent>
    </Card>
  )
}
